import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Shield, ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';
import { MFAEnrollment } from './MFAEnrollment';
import { useMFABackupCodes } from '@/hooks/useMFABackupCodes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';

export function MFAManager() {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [factors, setFactors] = useState<any[]>([]);
  const { remainingCodes, refreshCodes } = useMFABackupCodes();

  useEffect(() => {
    checkMFAStatus();
  }, []);

  const checkMFAStatus = async () => {
    setIsLoading(true);
    try {
      // Check profile for MFA status
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('mfa_enabled, mfa_enrolled_at')
        .eq('user_id', user.id)
        .single();

      setMfaEnabled(profile?.mfa_enabled || false);

      // Get enrolled factors
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      if (factorsData) {
        setFactors(factorsData.totp || []);
      }
    } catch (error) {
      console.error('Error checking MFA status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    setIsDisabling(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      // Unenroll all factors
      for (const factor of factors) {
        await supabase.auth.mfa.unenroll({ factorId: factor.id });
      }

      // Delete backup codes
      await supabase
        .from('mfa_backup_codes')
        .delete()
        .eq('user_id', user.id);

      // Update profile
      await supabase
        .from('profiles')
        .update({
          mfa_enabled: false,
          mfa_enrolled_at: null,
        })
        .eq('user_id', user.id);

      // Send notification
      await invokeEdgeFunction('mfa-notifications', {
        body: {
          type: 'disabled',
          userId: user.id,
        },
        callerOperation: 'MFAManager.disableNotify',
      });

      toast({
        title: 'Two-factor authentication disabled',
        description: 'Your account is no longer protected with 2FA',
        variant: 'destructive',
      });

      setMfaEnabled(false);
      setFactors([]);
      setShowDisableDialog(false);
    } catch (error) {
      console.error('Error disabling MFA:', error);
      toast({
        title: 'Failed to disable 2FA',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsDisabling(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (showEnrollment) {
    return (
      <MFAEnrollment
        onEnrollmentComplete={() => {
          setShowEnrollment(false);
          checkMFAStatus();
          refreshCodes();
        }}
      />
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Two-Factor Authentication</CardTitle>
              {mfaEnabled ? (
                <Badge variant="default" className="gap-1">
                  <ShieldCheck className="h-3 w-3" />
                  Enabled
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <ShieldAlert className="h-3 w-3" />
                  Disabled
                </Badge>
              )}
            </div>
          </div>
          <CardDescription>
            Add an extra layer of security to your account with two-factor authentication
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!mfaEnabled && (
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                We recommend enabling 2FA to protect your account from unauthorized access.
              </AlertDescription>
            </Alert>
          )}

          {mfaEnabled && factors.length > 0 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Enrolled devices:</p>
                {factors.map((factor) => (
                  <div
                    key={factor.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">
                        {factor.friendly_name || 'Authenticator App'}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {factor.factor_type.toUpperCase()}
                    </Badge>
                  </div>
                ))}
              </div>

              <Alert>
                <ShieldCheck className="h-4 w-4" />
                <AlertDescription>
                  You have {remainingCodes} backup code{remainingCodes !== 1 ? 's' : ''} remaining. 
                  {remainingCodes === 0 && ' Consider regenerating codes by disabling and re-enabling 2FA.'}
                  {remainingCodes < 3 && remainingCodes > 0 && ' Consider regenerating codes soon.'}
                </AlertDescription>
              </Alert>
            </div>
          )}

          <div className="flex gap-2">
            {!mfaEnabled ? (
              <Button onClick={() => setShowEnrollment(true)} className="w-full">
                <Shield className="mr-2 h-4 w-4" />
                Enable Two-Factor Authentication
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => setShowDisableDialog(true)}
                className="w-full"
              >
                Disable Two-Factor Authentication
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Two-Factor Authentication?</AlertDialogTitle>
            <AlertDialogDescription>
              This will make your account less secure. You can re-enable 2FA at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisableMFA}
              disabled={isDisabling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDisabling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disabling...
                </>
              ) : (
                'Disable 2FA'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
